import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Coins,
  ShieldCheck,
  Leaf,
  AlertCircle,
  CheckCircle2,
  Check,
  X,
  BookOpen,
} from "lucide-react";

const AboutUs = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const fadeIn = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="bg-[#FAF9F6] text-slate-800 min-h-screen">

      {/* ─────────────────────────────────────────────────────────────
          Hero Section (Original Untouched)
      ───────────────────────────────────────────────────────────── */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        <motion.img
          src="/images/about us img.png"
          alt="Modern library space"
          className="w-full h-full object-cover object-center"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-extrabold text-white text-center drop-shadow-lg px-4"
          >
            Revolutionizing Reading Books
          </motion.h1>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          1. ABOUT INTRODUCTION (Open Editorial Layout - No Boxed Card!)
      ───────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-14 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Main Story Narrative */}
          <div className="max-w-4xl mx-auto text-center space-y-6 mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
              About{" "}
              <span className="bg-gradient-to-r from-[#FF7236] to-[#E65100] bg-clip-text text-transparent">
                Books At Doorstep
              </span>
            </h2>

            <p className="text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed font-normal">
              <strong className="text-slate-900 font-semibold">Books At Doorstep</strong> is an initiative by{" "}
              <span className="text-slate-900 font-semibold">Codeverge IT Business Solutions</span>. It was launched with a vision to
              democratize reading by removing barriers like travel, access, and
              cost. We believe knowledge should be as easy to access as food
              delivery – and that’s what we’re making possible for book lovers
              across Pune. Our dedicated team curates and delivers books tailored
              to each reader’s preferences, so they can focus on what matters
              most—reading.
            </p>
          </div>

          {/* 4 Feature Columns (Editorial Pillars - Borderless) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 pt-4">
            {/* 1. Echo Credits */}
            <div className="relative pl-6 border-l-2 border-amber-400 hover:border-[#FF7236] transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Coins className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#FF7236] transition-colors">
                Echo Credits
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Earn and use Echo Credits for discounts and special offers
              </p>
            </div>

            {/* 2. Flexible Subscriptions */}
            <div className="relative pl-6 border-l-2 border-orange-400 hover:border-[#FF7236] transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#FF7236] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#FF7236] transition-colors">
                Flexible Subscriptions
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Choose from a variety of subscription plans that suit your reading habits
              </p>
            </div>

            {/* 3. Eco-Conscious Model */}
            <div className="relative pl-6 border-l-2 border-emerald-400 hover:border-[#FF7236] transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Leaf className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#FF7236] transition-colors">
                Eco-Conscious Model
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Each book serves 10+ readers, reducing paper waste by 75%
              </p>
            </div>

            {/* 4. Interactive Reading */}
            <div className="relative pl-6 border-l-2 border-indigo-400 hover:border-[#FF7236] transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#FF7236] transition-colors">
                Interactive Reading
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Dive into a next-gen reading experience with our titles.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          2. PROBLEM & SOLUTION (Harmonious Side-by-Side Comparison)
      ───────────────────────────────────────────────────────────── */}
      <motion.section
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ duration: 0.6 }}
        className="py-16 sm:py-12 bg-white border-b border-slate-200/60"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Bridging the Gap for Modern Readers
            </h2>
          </div>

          {/* Balanced Side-by-Side Comparison */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {/* The Problem Side */}
            <div className="bg-[#FCFBF8] rounded-3xl p-8 sm:p-10 lg:p-12 border border-red-100 flex flex-col justify-between hover:shadow-lg hover:shadow-red-950/5 transition-all duration-300">
              <div>
              
                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4 tracking-tight">
                  The Problem
                </h3>
                <p className="text-slate-600 leading-relaxed text-base sm:text-lg">
                  In today’s fast-paced digital world, people have less time and fewer
                  opportunities to visit libraries or bookstores. Physical access,
                  time constraints, and rising book prices are pushing readers away
                  from books. Additionally, the lack of customized reading experiences
                  often results in people losing interest in reading altogether.
                </p>

                {/* Scannable Friction Points */}
                <div className="space-y-2.5 pt-6 border-t border-slate-200/60 mt-6">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                      <X className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Commute & library travel time</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                      <X className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Rising book purchase prices</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                      <X className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Lack of personalized recommendations</span>
                  </div>
                </div>
              </div>
            </div>

            {/* The Solution Side */}
            <div className="bg-gradient-to-br from-orange-50/50 via-white to-amber-50/30 rounded-3xl p-8 sm:p-10 lg:p-12 border border-orange-200/70 shadow-md shadow-orange-950/5 flex flex-col justify-between hover:shadow-xl transition-all duration-300">
              <div>
              
                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4 tracking-tight">
                  The Solution
                </h3>
                <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
                  Books At Doorstep solves this problem by offering a personalized,
                  cost-effective, and time-saving solution. We bring the library to your
                  home with free delivery, a vast collection, and flexible rental plans.
                  Whether you want to read a bestseller, a regional language novel, or
                  academic content—we’ve got you covered.
                </p>

                {/* Scannable Benefit Points */}
                <div className="space-y-2.5 pt-6 border-t border-orange-200/50 mt-6">
                  <div className="flex items-center gap-3 text-sm text-slate-800 font-medium">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Free doorstep delivery & pickup across Pune</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-800 font-medium">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Affordable, flexible rental plans</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-800 font-medium">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </div>
                    <span>Curated catalog across 34+ genres</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </motion.section>

      {/* ─────────────────────────────────────────────────────────────
          3. WHY THIS INITIATIVE (Asymmetric Editorial Magazine Layout)
      ───────────────────────────────────────────────────────────── */}
      <motion.section
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="py-16 sm:py-12"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">

            {/* Visual Column */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-sm sm:max-w-md">
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-200/80 bg-white group">
                  <img
                    src="/images/book-flip.png"
                    alt="Sustainable reading concept"
                    className="w-full h-auto object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            {/* Content Column - Editorial List */}
            <div className="lg:col-span-7 space-y-6">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                Why This{" "}
                <span className="bg-gradient-to-r from-[#FF7236] to-[#E65100] bg-clip-text text-transparent">
                  Initiative
                </span>
              </h3>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
                Books at Doorstep aims to raise awareness about sustainable
                paper use and give new life to pre-loved books. Instead of letting
                books gather dust or end up in landfills, we circulate them among
                readers across Pune, extending each book’s lifecycle and reducing
                waste.
              </p>

              {/* Numbered Editorial Points - Clean Minimalist Rows */}
              <div className="divide-y divide-slate-200/80 pt-2">
                <div className="py-4 flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <span className="text-base font-black text-[#FF7236]">01</span>
                    <span className="text-base sm:text-lg font-bold text-slate-800 group-hover:text-[#FF7236] transition-colors">
                      Carbon-neutral delivery fleet
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-[#FF7236] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                </div>

                <div className="py-4 flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <span className="text-base font-black text-[#FF7236]">02</span>
                    <span className="text-base sm:text-lg font-bold text-slate-800 group-hover:text-[#FF7236] transition-colors">
                      Curated picks matched to your interests
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-[#FF7236] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                </div>

                <div className="py-4 flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <span className="text-base font-black text-[#FF7236]">03</span>
                    <span className="text-base sm:text-lg font-bold text-slate-800 group-hover:text-[#FF7236] transition-colors">
                      Flexible rental and buy options
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-[#FF7236] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </motion.section>

    </div>
  );
};

export default AboutUs;
